import React, { useContext, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { AllieLoading } from '@allie/app-ui-lib';
import { TokenValidationError } from '../../components/TokenValidationError';
import { useAllieAuth, useAppTranslations } from '../../hooks';
import { VALIDATE_APP_TOKEN } from '../../data';
import { UtilsContext } from '../../context';
import './ValidateTokenPage.scss';
import { TrackingEvents } from 'src/helpers/tracking-events';
import { LoginType } from 'src/helpers/enums';

interface TrackingData {
  [key: string]: string | number | boolean;
}

interface ValidateTokenPageProps {
  redirectUrl: string;
  token?: string;
  trackingEvent?: string;
  trackingData?: TrackingData;
  tokenContext?: string;
}

export const ValidateTokenPage: React.FC<ValidateTokenPageProps> = ({
  redirectUrl,
  token: propToken,
  trackingEvent,
  trackingData,
  tokenContext,
}) => {
  const { search } = useLocation();
  const params = useParams<Record<string, string | undefined>>();

  const { tracker, company } = useContext(UtilsContext);

  const { AUTHENTICATING, INVALID_TOKEN } = useAppTranslations();

  const token = propToken || new URLSearchParams(search).get('token');

  // Extract param key from redirectUrl
  const paramMatch = redirectUrl.match(/:(\w+)/);
  const paramKey = paramMatch ? paramMatch[1] : null;

  const { data, loading, error } = useQuery(VALIDATE_APP_TOKEN, {
    skip: !token,
    variables: { token, ...(tokenContext && { tokenContext }) },
  });

  const { simulateLogin } = useAllieAuth();

  useEffect(() => {
    if (data?.validateAppToken) {
      simulateLogin(data.validateAppToken, () => {
        let finalRedirectUrl = redirectUrl;
        let paramValue;

        if (paramKey && paramKey in params && params[paramKey]) {
          paramValue = params[paramKey];
          finalRedirectUrl = redirectUrl.replace(`:${paramKey}`, paramValue as string);
        }

        tracker?.track(TrackingEvents.LOGIN.login_user_loggedin, {
          user_name: data.validateAppToken.name,
          user_role: data.validateAppToken.role,
          user_client: company?.name || '',
          login_type: LoginType.AUTOLOGIN,
        });

        // Execute tracking event if provided
        if (trackingEvent && tracker && company) {
          // Merge provided tracking data with the URL parameter
          const mergedTrackingData = {
            company: company?.name,
            ...trackingData,
            ...(paramKey && paramValue ? { [paramKey]: paramValue } : {}),
          };

          tracker.track(trackingEvent, mergedTrackingData);
        }

        document.location.href = finalRedirectUrl;
      });
    }
  }, [data, redirectUrl, paramKey, params, tracker, company]);

  if (loading) {
    return <AllieLoading isOpen={loading} message={AUTHENTICATING} />;
  }

  if (error || data?.validateAppToken === null) {
    return <TokenValidationError message={INVALID_TOKEN} />;
  }

  return null;
};
